import { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { FirebaseError } from 'firebase/app';
import {
  AuthProvider,
  UserCredential,
  getAdditionalUserInfo,
  signInWithPopup,
} from 'firebase/auth';
import { FirebasePopupProvider, SOCIAL_AUTH_PROVIDER_LABEL } from 'constants/auth';
import { RequestStatus } from 'constants/requests';
import { auth, facebookProvider, googleProvider } from 'services/client/firebase';
import { StatePayloadValue, persistStatePayload } from 'services/client/oauthStatePayload';

/** Safely reads a Firebase error `code` (e.g. 'auth/popup-closed-by-user') off an unknown throw. */
export const getFirebaseErrorCode = (error: unknown): string | undefined =>
  error instanceof FirebaseError ? error.code : undefined;

// Only google + facebook are wired. `apple` is in FirebasePopupProvider for the future but has
// no provider yet — fail loudly rather than silently signing in with the wrong provider.
const getFirebaseProvider = (provider: FirebasePopupProvider): AuthProvider => {
  if (provider === 'google') {
    return googleProvider;
  }
  if (provider === 'facebook') {
    return facebookProvider;
  }
  throw new Error(`Unsupported OAuth provider: ${provider}`);
};

interface OAuthSignInResult {
  userCredential: UserCredential | null;
  isNewUser: boolean;
  error: Error | null;
  /** Firebase error code, so callers can branch (e.g. the account-linking flow). */
  code?: string;
  /** Raw Firebase error, so the linking flow can pull the pending credential + email. */
  firebaseError?: unknown;
}

export type { StatePayloadValue } from 'services/client/oauthStatePayload';

interface OAuthSignInOptions {
  /**
   * Values captured before the popup that must survive the round-trip (username,
   * account type, initial lat/lng). Read + cleared by the completion modal.
   */
  statePayload?: Record<string, StatePayloadValue>;
}

export const useOAuthSignIn = () => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.Idle);

  const signInWithProvider = async (
    providerKey: FirebasePopupProvider,
    options?: OAuthSignInOptions,
  ): Promise<OAuthSignInResult> => {
    setStatus(RequestStatus.InProgress);

    try {
      persistStatePayload(options?.statePayload);
      const result = await signInWithPopup(auth, getFirebaseProvider(providerKey));

      // Canonical new-vs-returning signal from Firebase. The metadata timestamps are only
      // second-precision, so a fast return visit could otherwise misclassify as new.
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;

      setStatus(RequestStatus.Success);

      return { userCredential: result, isNewUser, error: null };
    } catch (error) {
      setStatus(RequestStatus.Error);

      const providerLabel = SOCIAL_AUTH_PROVIDER_LABEL[providerKey];
      const code = getFirebaseErrorCode(error);
      let errorMessage = `Failed to sign in with ${providerLabel}`;

      switch (code) {
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
          errorMessage = 'Sign in was cancelled';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by your browser';
          break;
        case 'auth/account-exists-with-different-credential':
          // Handled by the caller's account-linking flow — leave a clear default message.
          errorMessage = 'An account already exists with this email';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = `${providerLabel} sign in is not enabled`;
          break;
        case 'auth/operation-not-supported-in-this-environment':
          errorMessage = `${providerLabel} sign in is not supported in this environment`;
          break;
        case 'auth/unauthorized-domain':
          errorMessage = `This domain is not authorized for ${providerLabel} sign in`;
          break;
        default:
          Sentry.captureException(error);
          break;
      }

      return {
        userCredential: null,
        isNewUser: false,
        error: new Error(errorMessage),
        code,
        firebaseError: error,
      };
    }
  };

  return {
    signInWithProvider,
    status,
    isLoading: status === RequestStatus.InProgress,
  };
};
